import * as THREE from 'three'

const LMB = 1 // bitmask
const RMB = 2 // bitmask

export class ClientInput {
  constructor(client) {
    this.client = client
    this.lmb = {
      down: false,
      pressed: false,
      released: false,
    }
    this.rmb = {
      down: false,
      pressed: false,
      released: false,
    }
    this.keys = {
      down: new Set(),
      pressed: new Set(),
      released: new Set(),
    }
    this.pointer = {
      coords: new THREE.Vector2(), // [0,0] to [1,1]
      position: new THREE.Vector2(), // [0,0] to [viewportWidth,viewportHeight]
      delta: new THREE.Vector2(), // position delta (pixels)
    }
    this.touches = new Map() // id -> touch
    this.touchesStarted = []  // touches that began this frame: [{ id, x, y }]
    this.touchesEnded = []    // touches that ended this frame: [{ id, x, y }]
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    this.client.viewport.addEventListener('pointerdown', this.onPointerDown)
    this.client.viewport.addEventListener('pointermove', this.onPointerMove)
    this.client.viewport.addEventListener('pointerup', this.onPointerUp)
    this.client.viewport.addEventListener('pointercancel', this.onPointerUp)
    this.client.viewport.addEventListener('touchstart', this.onTouchStart)
    document.body.addEventListener('contextmenu', this.onContextMenu)
    window.addEventListener('focus', this.onFocus)
    window.addEventListener('blur', this.onBlur)
  }

  key(code) {
    let value = this.keys[code]
    if (!value) {
      value = { down: false, pressed: false, released: false }
      this.keys[code] = value
    }
    return value
  }

  onKeyDown = e => {
    const key = this.keys[e.code]
    if (!key) return
    if (key.down) return
    key.down = true
    key.pressed = true
  }

  onKeyUp = e => {
    const key = this.keys[e.code]
    if (!key) return
    if (!key.down) return
    key.down = false
    key.released = true
  }

  onPointerDown = e => {
    if (e.pointerType === 'touch') {
      e.preventDefault()
      const touch = {
        id: e.pointerId,
        position: new THREE.Vector2(e.clientX, e.clientY),
        prevPosition: new THREE.Vector2(e.clientX, e.clientY),
        delta: new THREE.Vector2(),
        pointerType: e.pointerType,
      }
      this.touches.set(e.pointerId, touch)
      this.touchesStarted.push({ id: e.pointerId, x: e.clientX, y: e.clientY })
    }
    const rect = this.client.viewport.getBoundingClientRect()
    const offsetX = e.pageX - rect.left
    const offsetY = e.pageY - rect.top
    this.pointer.coords.x = Math.max(0, Math.min(1, offsetX / rect.width)) // prettier-ignore
    this.pointer.coords.y = Math.max(0, Math.min(1, offsetY / rect.height)) // prettier-ignore
    this.pointer.position.x = offsetX
    this.pointer.position.y = offsetY
    this.pointer.delta.x = 0
    this.pointer.delta.y = 0
    this.checkPointerChanges(e)
  }

  onPointerMove = e => {
    if (e.pointerType === 'touch') {
      const touch = this.touches.get(e.pointerId)
      if (touch) {
        touch.delta.x += e.clientX - touch.prevPosition.x
        touch.delta.y += e.clientY - touch.prevPosition.y
        touch.position.x = e.clientX
        touch.position.y = e.clientY
        touch.prevPosition.x = e.clientX
        touch.prevPosition.y = e.clientY
      }
    }
    const rect = this.client.viewport.getBoundingClientRect()
    const offsetX = e.pageX - rect.left
    const offsetY = e.pageY - rect.top
    this.pointer.coords.x = Math.max(0, Math.min(1, offsetX / rect.width)) // prettier-ignore
    this.pointer.coords.y = Math.max(0, Math.min(1, offsetY / rect.height)) // prettier-ignore
    this.pointer.position.x = offsetX
    this.pointer.position.y = offsetY
    this.pointer.delta.x += e.movementX
    this.pointer.delta.y += e.movementY
  }

  onPointerUp = e => {
    if (e.pointerType === 'touch') {
      const touch = this.touches.get(e.pointerId)
      if (touch) {
        this.touches.delete(e.pointerId)
      }
      this.touchesEnded.push({ id: e.pointerId, x: e.clientX, y: e.clientY })
    }
    this.checkPointerChanges(e)
  }

  onTouchStart = e => {
    e.preventDefault()
  }

  onContextMenu = e => {
    e.preventDefault()
  }

  onFocus = () => {
    this.releaseAll()
  }

  onBlur = () => {
    this.releaseAll()
  }

  releaseAll() {
    for (const code in this.keys) {
      const key = this.keys[code]
      if (key.down) {
        key.down = false
        key.released = true
      }
    }
  }

  checkPointerChanges(e) {
    const lmb = !!(e.buttons & LMB)
    // left mouse down
    if (!this.lmb.down && lmb) {
      this.lmb.down = true
      this.lmb.pressed = true
    }
    // left mouse up
    if (this.lmb.down && !lmb) {
      this.lmb.down = false
      this.lmb.released = true
    }
    const rmb = !!(e.buttons & RMB)
    // right mouse down
    if (!this.rmb.down && rmb) {
      this.rmb.down = true
      this.rmb.pressed = true
    }
    // right mouse up
    if (this.rmb.down && !rmb) {
      this.rmb.down = false
      this.rmb.released = true
    }
  }

  update(delta, time) {}

  lateUpdate(delta, time) {
    this.pointer.delta.set(0, 0, 0)
    this.lmb.pressed = false
    this.lmb.released = false
    this.rmb.pressed = false
    this.rmb.released = false
    for (const code in this.keys) {
      const key = this.keys[code]
      key.pressed = false
      key.released = false
    }
    this.pointer.pressed = false
    this.pointer.released = false
    this.touchesStarted.length = 0
    this.touchesEnded.length = 0
  }
}
